import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { QualityEvidenceFiles } from './QualityEvidenceFiles';

type Decision = 'keep_b' | 'rework' | 'scrap' | 'rtv';
interface Case {
  id: string; number: string; serial: string | null; productName: string; stage: string; result: 'B' | 'FAIL'; reasonLabel: string; reasonText: string | null;
  photos: string[]; raisedByName: string; suggestedScrapValue: number; bomRecoverables: Array<{ productId: string; name: string; qty: number }>; isTraded: boolean;
}
const STAGE: Record<string, string> = { puf_qa: 'PUF QA', packing_qa: 'Packing QA', final: 'Final inspection' };
const LABEL: Record<Decision, string> = { keep_b: 'Keep as B', rework: 'Rework', scrap: 'Scrap', rtv: 'Return to vendor' };
const surface = { borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' };

export function DispositionQueuePanel() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<{ c: Case; decision: Decision } | null>(null);
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [scrapValue, setScrapValue] = useState(0);
  const [recover, setRecover] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setCases((await api.get('/quality/dispositions', { params: { status: 'pending' } })).data.data); }
    catch (err: any) { setError({ code: err.response?.data?.code || 'QUEUE_LOAD_FAILED', message: err.response?.data?.error || 'Could not load the queue.' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = (c: Case, decision: Decision) => { setOpen({ c, decision }); setNote(''); setPhotos([]); setScrapValue(c.suggestedScrapValue); setRecover({}); setError(null); };

  const submit = async () => {
    if (!open) return;
    setSaving(true); setError(null);
    const body: Record<string, unknown> = { decision: open.decision, note, photos };
    if (open.decision === 'scrap') {
      body.scrapValue = scrapValue;
      body.recover = open.c.bomRecoverables.filter((r) => recover[r.productId]).map((r) => ({ productId: r.productId, qty: r.qty }));
    }
    try { await api.post(`/quality/dispositions/${open.c.id}/decide`, body); setOpen(null); load(); }
    catch (err: any) { setError({ code: err.response?.data?.code || 'DECISION_SAVE_FAILED', message: err.response?.data?.error || 'The decision was not saved.' }); }
    finally { setSaving(false); }
  };

  const options = (c: Case): Decision[] => [...(c.result === 'B' ? ['keep_b', 'rework'] : ['keep_b', 'rework', 'scrap']) as Decision[], ...(c.isTraded ? ['rtv' as Decision] : [])];

  return (
    <div className="mb-6 rounded-xl border p-4" style={surface}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Waiting for QA management {!loading && !error && `(${cases.length})`}</h2>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Photo and reason required for every decision. Only the QA Manager decides.</span>
      </div>
      {error && !open && <div role="alert" data-error-code={error.code}>{error.message}</div>}
      {error && !open ? null : loading ? <Loader2 size={16} className="animate-spin" /> : cases.length === 0 ? (
        <p className="py-4 text-sm" style={{ color: 'var(--text-muted)' }}>Nothing waiting</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-medium uppercase tracking-wider" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-muted)' }}>
                <th className="px-4 py-3">Serial</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3">Result</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Photos</th><th className="px-4 py-3">Decide</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} className="border-b" style={{ borderColor: 'var(--border-primary)' }}>
                  <td className="px-4 py-3 font-mono text-xs">{c.serial ?? c.number}</td>
                  <td className="px-4 py-3">{STAGE[c.stage] ?? c.stage}</td>
                  <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${c.result === 'B' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{c.result === 'B' ? 'Grade B' : 'Fail'}</span></td>
                  <td className="px-4 py-3">{c.reasonLabel}{c.reasonText ? ` — ${c.reasonText}` : ''}</td>
                  <td className="px-4 py-3">{c.photos.length}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {options(c).map((d) => (
                        <button key={d} onClick={() => start(c, d)} className="rounded-lg border px-2.5 py-1 text-xs font-medium hover:bg-[var(--bg-inset)]" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}>{LABEL[d]}</button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(null)}>
          <div role="dialog" aria-modal="true" aria-label={LABEL[open.decision]} className="max-h-[90vh] overflow-y-auto w-full max-w-lg rounded-xl border p-5 shadow-xl" style={surface} onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{LABEL[open.decision]} {open.c.serial ?? open.c.number}</h3>
              <button onClick={() => setOpen(null)} aria-label="Close"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              {open.decision === 'scrap' && (
                <>
                  <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>Value to Scrap Yard (₹)
                    <input aria-label="Value to Scrap Yard (₹)" type="number" min={0} value={scrapValue} onChange={(e) => setScrapValue(Number(e.target.value))} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)' }} />
                  </label>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatINR(scrapValue)}</p>
                  {open.c.bomRecoverables.length > 0 && (
                    <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <p className="mb-1">Recover to stock (from BOM)</p>
                      {open.c.bomRecoverables.map((r) => (
                        <label key={r.productId} className="mr-4 inline-flex items-center gap-1.5">
                          <input type="checkbox" aria-label={`Recover ${r.name} ×${r.qty}`} checked={!!recover[r.productId]} onChange={(e) => setRecover((p) => ({ ...p, [r.productId]: e.target.checked }))} />
                          {r.name} ×{r.qty}
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
              <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>Reason *
                <textarea aria-label="Reason *" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)' }} />
              </label>
              <QualityEvidenceFiles files={photos} onChange={setPhotos} entityId={open.c.id} />
              {error && <div role="alert" data-error-code={error.code} className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"><AlertTriangle size={14} />{error.message}</div>}
              <div className="flex justify-end gap-2">
                <button onClick={() => setOpen(null)} className="rounded-lg border px-4 py-2 text-sm" style={{ borderColor: 'var(--border-primary)' }}>Cancel</button>
                <button onClick={submit} disabled={saving} className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">{`Confirm ${open.decision === 'keep_b' ? 'keep as B' : LABEL[open.decision].toLowerCase()}`}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
